import { isNumber } from 'lodash'

import { CatalogAttribute, Filter } from '@marketplace-web/catalog/catalog-data'
import type { TrackingEvent } from '@marketplace-web/observability/event-tracker-data'

// Source: https://github.com/vinted/dwh-schema-registry/blob/5387d2ce7b44ee93711a15dfb97c60a4d984f22c/avro/events/common/shared/user.avdl#L25
type UserTarget =
  | 'add_item_to_bundle'
  | 'remove_item_from_bundle'
  | 'report_profile'
  | 'filter_sorting_selection'
  | 'filter_active'
  | 'filter_reserved'
  | 'filter_hidden'
  | 'filter_drafts'
  | 'filter_sold'
  | 'feedback_tab'
  | 'profile_tab'
  | 'about_tab'
  // TODO: add it to the dwh-schema-registry
  | 'insights_tab'
  // TODO: add it to the dwh-schema-registry
  | 'follow'
  | 'followers'
  | 'following'

// Source: https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/common/shared/common.avdl#L109
type ContentSource = 'user_items'
// Source: https://github.com/vinted/dwh-schema-registry/blob/4e0abdfa54043d3ce75c45607c6352d49397d8a7/avro/events/common/shared/common.avdl#L34-L109
type ContentTypes = 'item'

type viewUserEventArgs = {
  path: string
  user_id: number
}

export const viewUserEvent = (args: viewUserEventArgs): TrackingEvent => {
  const { path, user_id } = args

  const extra = {
    path,
    user_id,
  }

  return {
    event: 'user.view_user',
    extra,
  }
}

type ClickEventArgs = {
  target: UserTarget
  screen?: string | null
  targetDetails?: string | null
  path?: string | null
  env?: string | null
}

type ClickEventExtra = {
  target: UserTarget
  screen?: string
  target_details?: string
  path?: string | null
  env?: string | null
}

export const clickEvent = (args: ClickEventArgs): TrackingEvent => {
  const { target, screen, targetDetails, env, path } = args
  const extra: ClickEventExtra = { target }

  if (screen) extra.screen = screen

  if (targetDetails) extra.target_details = targetDetails

  if (env) extra.env = env

  if (path) extra.path = path

  return {
    event: 'user.click',
    extra,
  }
}

type FollowUserEventArgs = {
  userId: number
  isFollowEvent: boolean
}

type FollowUserEventExtra = {
  user_id: number
}

export const followUserEvent = ({ userId, isFollowEvent }: FollowUserEventArgs): TrackingEvent => {
  const eventName = isFollowEvent ? 'user.follow_user' : 'user.unfollow_user'

  const extra: FollowUserEventExtra = {
    user_id: userId,
  }

  return {
    event: eventName,
    extra,
  }
}

type ImpressionEventArgs = {
  id: number | string
  contentType: ContentTypes
  contentSource: ContentSource
  position: number
  itemDistance?: number | null
  itemOwnerId?: string | number | null
  searchScore?: string
  searchSignals?: Array<string> | null
  searchCorrelationId?: string
  searchSessionId?: string | null
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  homepageSessionId?: string
  uploadSessionId?: string
  screen?: string
  metadata?: Record<string, Array<string>>
}

type ImpressionEventExtra = {
  content_type: ContentTypes
  content_source: ContentSource
  id: number | string
  position: number
  item_owner_id?: string | number
  search_score?: string
  search_signals?: Array<string>
  search_correlation_id?: string
  search_session_id?: string
  global_search_session_id?: string | null
  global_catalog_browse_session_id?: string | null
  buyer_item_distance_km?: number
  homepage_session_id?: string
  upload_session_id?: string
  screen?: string
  metadata?: Record<string, Array<string>>
}

export const impressionEvent = (args: ImpressionEventArgs): TrackingEvent => {
  const {
    id,
    contentType,
    contentSource,
    itemDistance,
    itemOwnerId,
    position,
    searchScore,
    searchSignals,
    searchCorrelationId,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    homepageSessionId,
    uploadSessionId,
    screen,
    metadata,
  } = args

  const extra: ImpressionEventExtra = {
    id,
    position,
    content_type: contentType,
    content_source: contentSource,
  }

  if (itemOwnerId) extra.item_owner_id = itemOwnerId
  if (searchScore) extra.search_score = JSON.stringify(searchScore)
  if (searchSignals) extra.search_signals = searchSignals
  if (searchCorrelationId) extra.search_correlation_id = searchCorrelationId
  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (isNumber(itemDistance)) extra.buyer_item_distance_km = itemDistance
  if (homepageSessionId) extra.homepage_session_id = homepageSessionId
  if (uploadSessionId) extra.upload_session_id = uploadSessionId
  if (screen) extra.screen = screen
  if (metadata) extra.metadata = metadata

  return {
    event: 'list.show_item',
    extra,
  }
}

// Source: https://github.com/vinted/dwh-schema-registry/blob/master/avro/events/profile/click.avdl#L25
type ProfileTarget = 'view_all_user_items'

type ProfileClickEventArgs = {
  target: ProfileTarget
  ownerId: string
  screen?: string
  itemId?: string
}

type ProfileClickEventExtra = {
  target: ProfileTarget
  owner_id: string
  screen?: string
  item_id?: string
}

export const profileClickEvent = (args: ProfileClickEventArgs): TrackingEvent => {
  const { target, ownerId, screen, itemId } = args

  const extra: ProfileClickEventExtra = { target, owner_id: ownerId }

  if (screen) extra.screen = screen
  if (itemId) extra.item_id = itemId

  return {
    event: 'profile.click',
    extra,
  }
}

type ViewScreenEventArgs = {
  screen: string
}

export const viewScreenEvent = (args: ViewScreenEventArgs): TrackingEvent => {
  const { screen } = args

  const extra = {
    screen,
  }

  return {
    event: 'user.view_screen',
    extra,
  }
}

type ClickFilterEventArgs = {
  filter: Filter
  screen?: string
  searchSessionId?: string
  globalSearchSessionId?: string | null
  globalCatalogBrowseSessionId?: string | null
  type?: string
  code?: string
}

type ClickFilterEventExtra = {
  filter: Filter
  screen?: string
  search_session_id?: string
  global_search_session_id?: string
  global_catalog_browse_session_id?: string | null
  type?: string
  code?: string
}

export const clickFilterEvent = (args: ClickFilterEventArgs): TrackingEvent => {
  const {
    filter,
    screen,
    searchSessionId,
    globalSearchSessionId,
    globalCatalogBrowseSessionId,
    type,
    code,
  } = args
  const extra: ClickFilterEventExtra = {
    filter,
    screen,
  }

  if (searchSessionId) extra.search_session_id = searchSessionId
  if (globalSearchSessionId) extra.global_search_session_id = globalSearchSessionId
  if (globalCatalogBrowseSessionId)
    extra.global_catalog_browse_session_id = globalCatalogBrowseSessionId
  if (type) extra.type = type
  if (code) extra.code = code

  return {
    event: 'user.click_filter',
    extra,
  }
}

type SelectCatalogEventArgs = {
  catalogIds: Array<number>
  attributeId?: CatalogAttribute
  screen?: string
}

type SelectCatalogEventExtra = {
  catalog_id: string
  attribute_id: number | null
  screen?: string
}

export const selectCatalogEvent = (args: SelectCatalogEventArgs): TrackingEvent => {
  const { catalogIds, attributeId, screen } = args

  const extra: SelectCatalogEventExtra = {
    catalog_id: catalogIds.join(','),
    attribute_id: attributeId ?? null,
  }

  if (screen) extra.screen = screen

  return {
    event: 'user.select_catalog',
    extra,
  }
}
